import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { PARTNER_ENTRANCE } from '../../util/partnerEntrance';

import { NamedLink } from '../../components';

import CategoryFlyout from './CategoryFlyout';
import { getCategoryIcon } from './icons';
import IconStore from './icons/IconStore';
import { visibleCategories, categorySearchString } from './helpers';

import css from './CategoryMenu.module.css';

// Small open / close delays make the hover behaviour feel intentional.
// Opening too aggressively triggers panels while the user is scanning
// past; closing too aggressively dismisses the panel before the buyer
// can move their mouse down into it.
const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 200;

/**
 * Category navigation row that mounts beneath the main Topbar. Each
 * Level 1 category renders as a `NamedLink` to SearchPage with the
 * matching `pub_categoryLevel1` param; hovering reveals the L2/L3
 * flyout. Categories flagged `hidden: true` in the configuration are
 * skipped entirely (deep links still work — only the menu entry is
 * omitted).
 *
 * Read via `useConfiguration().categoryConfiguration` so a hosted-asset
 * edit can swap the category tree without a code deploy.
 *
 * @param {Object} props
 * @param {string} [props.className] root override
 * @param {string} [props.rootClassName] root override (Sharetribe pattern)
 */
const CategoryMenu = props => {
  const { className, rootClassName } = props;
  const intl = useIntl();
  const config = useConfiguration();

  const [activeId, setActiveId] = useState(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Clean up any pending timers on unmount so a delayed open / close
  // doesn't fire against a stale component.
  useEffect(() => {
    return () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const cancelPending = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleOpen = id => {
    cancelPending();
    openTimerRef.current = setTimeout(() => {
      setActiveId(id);
      openTimerRef.current = null;
    }, OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    cancelPending();
    closeTimerRef.current = setTimeout(() => {
      setActiveId(null);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  const handleRequestClose = () => {
    cancelPending();
    setActiveId(null);
  };

  const categoriesRaw = config?.categoryConfiguration?.categories || [];
  const categories = visibleCategories(categoriesRaw);

  if (categories.length === 0) {
    return null;
  }

  const activeCategory = activeId ? categories.find(c => c.id === activeId) || null : null;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'CategoryMenu.ariaLabel' })}
      onMouseLeave={scheduleClose}
    >
      <div className={css.inner}>
        <ul className={css.levelOneRow}>
          {categories.map(category => {
            const isActive = activeId === category.id;
            const Icon = getCategoryIcon(category.iconName);
            return (
              <li
                key={category.id}
                className={css.levelOneItem}
                onMouseEnter={() => scheduleOpen(category.id)}
                onClick={handleRequestClose}
              >
                {/* NamedLink doesn't forward onClick to its inner Link;
                    the handler lives on the <li> and the click bubbles
                    up from the anchor. */}
                <NamedLink
                  className={classNames(css.levelOneLink, {
                    [css.levelOneLinkActive]: isActive,
                  })}
                  name="SearchPage"
                  to={{ search: categorySearchString([category.id]) }}
                >
                  {Icon ? <Icon className={css.levelOneIcon} /> : null}
                  <span>{category.name}</span>
                </NamedLink>
              </li>
            );
          })}
          {/* After the categories: the way into Zuprize Partner. No flyout,
              so hovering it closes any open category. */}
          <li className={css.levelOneItem} onMouseEnter={scheduleClose}>
            <NamedLink className={css.levelOneLink} name={PARTNER_ENTRANCE.routeName}>
              <IconStore className={css.levelOneIcon} />
              <span>
                <FormattedMessage id={PARTNER_ENTRANCE.labelId} />
              </span>
            </NamedLink>
          </li>
        </ul>
      </div>

      {activeCategory ? (
        <div
          className={css.flyoutWrapper}
          onMouseEnter={cancelPending}
          onMouseLeave={scheduleClose}
        >
          {/* `key` forces a remount when the active Level 1 changes so
              CategoryFlyout's initial state (default-open first
              branch) recomputes for the new category instead of
              leaking across hovers. */}
          <CategoryFlyout
            key={activeCategory.id}
            category={activeCategory}
            onRequestClose={handleRequestClose}
          />
        </div>
      ) : null}
    </nav>
  );
};

export default CategoryMenu;
